import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Header } from '../../components/shared/header/header';
import { LeadsTable, LeadColumn, LeadRow, FilterConfig, ServerSidePagination } from '../../components/shared/leads-table/leads-table';
import { LeadsFilaService } from '../../services/leads-fila.service';
import { LeadsFilaItem, LeadsFilaRequest } from '../../interfaces/leads-fila.interface';
import { catchError, of, Subscription } from 'rxjs';
import { SelectedDnService } from '../../services/selected-dn.service';
import { FilterFieldsUtil } from '../../utils/filter-fields-utils';
import { availableOriginsType } from '../../components/shared/filter-fields/models/filter-field.model';
import { DashboardStoreService } from '../../services/store/dashboard.store.service';

@Component({
  selector: 'app-rebuying-period',
  standalone: true,
  imports: [CommonModule, RouterModule, Header, LeadsTable],
  templateUrl: './rebuying-period.html',
  styleUrls: ['./rebuying-period.scss']
})
export class RebuyingPeriod implements OnInit, OnDestroy, AfterViewInit {
  private dnSubscription?: Subscription;
  private hasLoadedInitialData = false;
  private hasReceivedInitialDates = false;
    availableFilterOrigins: availableOriginsType[] = ['sempre-novo', 'ofertas-vw', 'campanha'];

  tableColumns: LeadColumn[] = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'dataTerminoContrato', label: 'Data término de contrato', type: 'text' },
    { key: 'statusContrato', label: 'Status do contrato', type: 'text' },
    { key: 'disponivelDesde', label: 'Disponível desde', type: 'text' },
    { key: 'retornarEm', label: 'Retornar em', type: 'text' },
    { key: 'status', label: 'Status do Lead', type: 'status' }
  ];

  filterConfig: FilterConfig = {
    searchFields: ['nome'],
    alphabeticalField: 'NM_NOME',
    filterFields: {
      renewal: 'renovaMais',
      origin: 'origem'
    },
    dateField: 'dataTerminoContrato'
  };

  tableData: LeadRow[] = [];
  loading: boolean = false;
  loadingMessage: string = 'Carregando dados...';
  errorMessage: string = '';
  emptyMessage: string = 'Nenhum lead encontrado para o tipo de campanha informada.';
  serverSidePagination?: ServerSidePagination;

  private readonly tipoCampanha = 3;

  private currentSearchText: string | undefined = undefined;
  private currentFilters: string[] = [];
  private currentDateRange: {start: string, end: string} = {start: '', end: ''};
  private currentSortField: string = 'DT_CONTR_FIM';
  private currentSortOrder?: 'ASC' | 'DESC' = 'DESC';

  constructor(
    private leadsFilaService: LeadsFilaService,
    private readonly dashboardStoreService: DashboardStoreService,
    private router: Router,
    private selectedDnService: SelectedDnService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    this.currentDateRange = {
      start: this.formatDateForInput(threeMonthsAgo),
      end: this.formatDateForInput(today)
    };

    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (dn && this.hasReceivedInitialDates) {
        this.loadData(1, this.serverSidePagination?.pageSize || 20);
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.hasLoadedInitialData && !this.hasReceivedInitialDates) {
        this.hasReceivedInitialDates = true;
        this.loadData();
      }
    }, 200);
  }

  ngOnDestroy(): void {
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  loadData(
    page: number = 1,
    pageSize: number = 20
  ): void {
    this.loading = true;
    this.loadingMessage = 'Carregando período de recompra...';
    this.errorMessage = '';
    this.emptyMessage = 'Nenhum lead encontrado para o tipo de campanha informada.';

    const request: LeadsFilaRequest = {
      tipoCampanha: this.tipoCampanha,
      dn: this.selectedDnService.getSelectedDnAsNumber(),
      renovaMais: FilterFieldsUtil.getRenovaMaisFromFilters(this.currentFilters),
      origem: FilterFieldsUtil.getOrigemFromFilters(this.currentFilters),
      nome: this.currentSearchText,
      dataInicio: this.parseDate(this.dashboardStoreService.startDate()),
      dataFim: this.parseDate(this.dashboardStoreService.endDate()),
      page: page,
      pageSize: pageSize,
      sortField: this.currentSortField,
      sortOrder: this.currentSortOrder
    };

    this.leadsFilaService.getLeadsFila(request).pipe(
      catchError((error) => {
        if (error.status === 401) {
          this.errorMessage = 'Não autorizado. Redirecionando...';
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 2000);
        } else if (error.status === 204) {
          this.emptyMessage = 'Nenhum lead encontrado para o tipo de campanha informada.';
          this.tableData = [];
          this.loading = false;
        } else {
          this.errorMessage = 'Erro ao carregar dados. Tente novamente.';
          this.loading = false;
        }

        return of(null);
      })
    ).subscribe((response) => {
      if (response && response.data && response.data.length > 0) {
        this.tableData = this.mapApiDataToTableData(response.data);
        this.serverSidePagination = {
          totalRecords: response.pagination.totalRecords,
          page: response.pagination.page,
          pageSize: response.pagination.pageSize,
          from: response.pagination.from,
          to: response.pagination.to
        };
        this.hasLoadedInitialData = true;
        this.loading = false;
      } else {
        this.emptyMessage = response?.message || 'Nenhum lead encontrado para o tipo de campanha informada.';
        this.tableData = [];
        this.hasLoadedInitialData = true;
        this.loading = false;
      }
    });
  }

  onDateRangeChange(dateRange: {start: string, end: string}): void {
    this.hasReceivedInitialDates = true;

    if (dateRange.start && dateRange.end) {
      this.currentDateRange = dateRange;
    }

    this.loadData();
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private mapApiDataToTableData(apiData: LeadsFilaItem[]): LeadRow[] {
    return apiData.map((item) => {
      return {
        idLead: item.idLead,
        nome: item.nomeCliente || '-',
        statusContrato: item.statusContrato || '-',
        dataTerminoContrato: (item.terminoContrato && item.terminoContrato !== 'null' && item.terminoContrato !== 'undefined') ? item.terminoContrato : '-',
        disponivelDesde: (item.disponivelDesde && item.disponivelDesde !== 'null' && item.disponivelDesde !== 'undefined') ? item.disponivelDesde : '-',
        retornarEm: (item.retornarEm && item.retornarEm !== 'null' && item.retornarEm !== 'undefined') ? item.retornarEm : '-',
        status: item.statusLead || '-',
        renewal: this.mapRenovaMais(item),
      };
    });
  }

  private mapRenovaMais(item: LeadsFilaItem): string {
    return item.renovaMais === true ? 'renewalBlue' : 'renewalGrey';
  }

  onSortChange(sort: {field: string, order: 'ASC' | 'DESC'}): void {
    this.currentSortField = sort.field;
    this.currentSortOrder = sort.order;
    this.loadData(1, this.serverSidePagination?.pageSize || 20);
  }

  onSearchChange(searchText: string): void {
    this.currentSearchText = searchText;
    this.loadData();
  }

  onFilterChange(filters: string[]): void {
    this.currentFilters = filters;
    this.loadData();
  }

  onClearAllFilters(): void {
    this.currentSearchText = '';
    this.currentFilters = [];

    this.currentDateRange = {start: '', end: ''};
    this.currentSortField = 'DT_CONTR_FIM';
    this.currentSortOrder = 'DESC';
    this.hasReceivedInitialDates = false;

    this.loadData();
  }

  private parseDate(dateString: string): Date | undefined {
    if (!dateString || dateString.trim() === '') {
      return undefined;
    }

    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    if (dateString.includes('-')) {
      return new Date(dateString);
    }
    return undefined;
  }

  onPageSizeChange(newPageSize: number): void {
    this.loadData(1, newPageSize);
  }

  onPageChange(newPage: number): void {
    if (this.serverSidePagination) {
      this.loadData(newPage, this.serverSidePagination.pageSize);
    }
  }

  onViewLead(lead: LeadRow): void {
    if (lead.idLead) {
      this.router.navigate([`/leads/${lead.idLead}/detalhes`]);
    }
  }
}
